import {
  Box,
  Card,
  CardHeader,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { useListCustomerTransactionsQuery } from 'api/transactions/transactions';
import { Customer } from 'api/user-domain/user-domain.types';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from 'util/currency';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import TransactionDetailsDrawer from 'components/drawers/transaction-details-drawer/transaction-details-drawer.component';
import IncDataGrid from 'components/inc-data-grid/inc-data-grid.component';
import { Transaction } from 'api/transactions/transactions.types';
import { GridColDef } from '@mui/x-data-grid-premium';

type CustomerRecentTransactionGridProps = {
  customer: Customer;
  detailDrawer?: boolean;
  count?: number;
  close?: () => void;
};

const CustomerRecentTransactionDataGrid = ({
  customer,
  detailDrawer = false,
  count = 10,
  close,
}: CustomerRecentTransactionGridProps) => {
  const navigate = useNavigate();

  const {
    open,
    close: drawerClose,
    drawerData,
    setOpen,
    setDrawerData,
  } = useUtilityDrawer();

  const handleTransactionDetailsDrawer = params => {
    setDrawerData(params.row);
    setOpen(true);
  };

  const { data: rows } = useListCustomerTransactionsQuery({
    userid: customer?.userId,
    count: count.toString(),
    page: '0',
  });

  const transactionColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'txDateTime',
        headerName: t('page.customers.overview.date'),
        minWidth: 170,
        sortable: false,
        hideable: false,
        disableColumnMenu: true,
        valueFormatter: value =>
          DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED),
      },
      {
        field: 'total',
        minWidth: 110,
        headerName: t('page.customers.overview.basketValue'),
        sortable: false,
        hideable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => {
          return formatCurrency(row?.total?.total, 'USD');
        },
      },
      {
        field: 'storeName',
        minWidth: 180,
        headerName: t('page.customers.overview.location'),
        flex: 1,
        sortable: false,
        hideable: false,
        disableColumnMenu: true,
        hide: detailDrawer,
      },
      {
        field: 'source',
        headerName: t('page.customers.overview.source'),
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
      },
    ],
    [detailDrawer],
  );

  return (
    <>
      <Card>
        <CardHeader
          sx={{ paddingLeft: '10px' }}
          title={
            <Stack direction="row" justifyContent="space-between">
              <Typography gutterBottom color="text.secondary">
                {t('page.customers.overview.recentTransactions')}
              </Typography>
              <MuiLink
                href="#transactions"
                underline="none"
                onClick={() => {
                  navigate(`/crm/customers/${customer.userId}`);
                  close?.();
                }}
              >
                <Typography variant="body2">
                  {t('page.customers.overview.viewMore')}
                </Typography>
              </MuiLink>
            </Stack>
          }
        />
        <Box p={1}>
          <IncDataGrid
            columns={transactionColumns}
            rows={rows?.transactions ?? []}
            getRowId={row => row.id}
            onRowClick={params => handleTransactionDetailsDrawer(params)}
            data-testid="IncDataGrid.RecentTransactionGrid"
            hideFooter
            dataGridType="secondary"
          />
        </Box>
      </Card>
      <TransactionDetailsDrawer
        open={open}
        close={drawerClose}
        drawerData={drawerData as Transaction}
      />
    </>
  );
};
export default CustomerRecentTransactionDataGrid;
